import { ArrowLeft, Pencil, Star, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Markdown } from '@/components/ui/markdown';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { ApiError } from '@/core/api';
import { INTERVIEW_OUTCOME_LABEL, type InterviewOutcome } from '../constants';
import { useCandidate, useDeleteInterview, useInterview } from '../queries';
import { candidateName } from '../types';

const INTERVIEWS_PATH = '/hrm/recruitment/interviews';

const OUTCOME_VARIANT: Record<InterviewOutcome, 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  passed: 'secondary',
  failed: 'destructive',
};

export function InterviewDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: interview, isPending, isError, error } = useInterview(id);
  const candidate = useCandidate(interview?.candidate ?? '');
  const remove = useDeleteInterview();

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(INTERVIEWS_PATH)}>
          <ArrowLeft /> Interviews
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const name = candidate.data
    ? candidateName(candidate.data)
    : `Candidate #${interview.candidate}`;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(INTERVIEWS_PATH)}>
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Interview — {name}</h1>
        <Badge variant={OUTCOME_VARIANT[interview.outcome]}>
          {INTERVIEW_OUTCOME_LABEL[interview.outcome]}
        </Badge>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`${INTERVIEWS_PATH}/${interview.public_id}/edit`)}
          >
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete interview?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will remove the scheduled interview. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(interview.id, { onSuccess: () => navigate(INTERVIEWS_PATH) })
                  }
                >
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Stage">{interview.stage_name || '—'}</Field>
          <Field label="Scheduled">
            {interview.scheduled_at ? new Date(interview.scheduled_at).toLocaleString() : '—'}
          </Field>
          <Field label="Created">{new Date(interview.created).toLocaleDateString()}</Field>
        </CardContent>
      </Card>

      {interview.criteria_ratings.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Scorecard</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y rounded-md border">
              {interview.criteria_ratings.map((r) => (
                <div
                  key={r.criterion}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span>{r.criterion}</span>
                  <span className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={cn(
                          'size-4',
                          n <= r.score
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-muted-foreground/40',
                        )}
                      />
                    ))}
                    <span className="text-muted-foreground ml-2 w-8 text-right text-xs tabular-nums">
                      {r.score}/5
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          {interview.notes ? (
            <Markdown>{interview.notes}</Markdown>
          ) : (
            <p className="text-muted-foreground text-sm">No notes.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
